import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/client/components/ui/alert-dialog';
import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';

type FinanceRecord = RouterOutputs['finance']['listRecords'][number];

type Props = {
  records: FinanceRecord[];
  onEdit: (record: FinanceRecord) => void;
};

function formatDateShort(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function FinanceTable({ records, onEdit }: Props) {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const utils = trpc.useUtils();

  const deleteMutation = trpc.finance.deleteRecord.useMutation({
    onSuccess: () => {
      utils.finance.listRecords.invalidate();
      utils.finance.latestRecords.invalidate();
      setDeleteId(null);
      setDeleteError(null);
    },
    onError: (err) => setDeleteError(err.message),
  });

  if (records.length === 0) {
    return <div className="py-12 text-center text-sm text-muted-foreground">No records found.</div>;
  }

  return (
    <>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
            <th className="px-5 py-3 font-medium">Date</th>
            <th className="px-5 py-3 font-medium">Type</th>
            <th className="px-5 py-3 font-medium">Category</th>
            <th className="px-5 py-3 font-medium">Note</th>
            <th className="px-5 py-3 text-right font-medium">Amount</th>
            <th className="px-5 py-3" />
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.id} className="border-b last:border-0 transition-colors hover:bg-muted/30">
              <td className="whitespace-nowrap px-5 py-3 tabular-nums text-muted-foreground">
                {formatDateShort(r.date)}
              </td>
              <td className="px-5 py-3">
                {r.type === 'INCOME' ? (
                  <Badge className="gap-1.5 border-0 bg-good-bg text-good">
                    <span className="h-1.5 w-1.5 rounded-full bg-good" />
                    Income
                  </Badge>
                ) : (
                  <Badge variant="outline">Outcome</Badge>
                )}
              </td>
              <td className="px-5 py-3">{r.category}</td>
              <td className="px-5 py-3">
                <div
                  className="max-w-[200px] truncate text-xs text-muted-foreground"
                  title={r.description}
                >
                  {r.description}
                </div>
              </td>
              <td className="px-5 py-3 text-right">
                <span
                  className={`tabular-nums font-semibold ${r.type === 'INCOME' ? 'text-good' : ''}`}
                >
                  {r.type === 'INCOME' ? '+' : '–'}€{formatAmount(r.amount)}
                </span>
              </td>
              <td className="px-5 py-3">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit record"
                    onClick={() => onEdit(r)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete record"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      setDeleteId(r.id);
                      setDeleteError(null);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <AlertDialog
        open={!!deleteId}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteId(null);
            setDeleteError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <p className="px-6 text-sm text-destructive">{deleteError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteId && deleteMutation.mutate({ id: deleteId })}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
